import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Activity, ArrowRight, ShieldAlert, UserPlus, KeyRound, Mail, Building2, Trash2 } from 'lucide-react';
import api from '../../lib/api';

/**
 * RecentAdminActivityStrip — compact "what just happened?" feed.
 *
 * Renders the 5 most recent admin-relevant audit entries (user
 * created / role changed / invitation resent / org created / etc.)
 * directly under the hero of the Org & User Management page so two-
 * admin shops can spot peer activity without opening the full Audit
 * Log tab. The whole strip is wrapped in a button-shaped link that
 * the parent passes in via `onOpenAuditLog` — clicking anywhere on
 * the strip switches the parent's tab to "audit" instead of
 * navigating away (preserves state on the management page).
 *
 * Backed by GET /api/rbac/audit-logs?limit=5 with the `users` /
 * `roles` / `orgs` categories — anything else (data imports,
 * dashboard views) is too noisy for this surface and lives in the
 * full audit log instead.
 *
 * Self-hides when the API returns nothing (fresh install) or when
 * the caller lacks `audit.view.{all,org}` permission (403 → empty).
 */

// Per-action presentation. Add new actions here as they're audited
// — the strip falls back to a generic record icon for anything
// unmapped so it never crashes on an unknown action.
const ACTION_PRESENTATION = {
  'user.created':   { icon: UserPlus,    label: 'created user',    tone: 'text-emerald-700 bg-emerald-50' },
  'user.invited':   { icon: Mail,        label: 'invited user',    tone: 'text-teal-700 bg-teal-50' },
  'user.updated':   { icon: ShieldAlert, label: 'updated user',    tone: 'text-slate-700 bg-slate-100' },
  'user.deleted':   { icon: Trash2,      label: 'deleted user',    tone: 'text-rose-700 bg-rose-50' },
  'role.assigned':  { icon: KeyRound,    label: 'assigned role',   tone: 'text-amber-700 bg-amber-50' },
  'role.revoked':   { icon: KeyRound,    label: 'revoked role',    tone: 'text-rose-700 bg-rose-50' },
  'org.created':    { icon: Building2,   label: 'created org',     tone: 'text-indigo-700 bg-indigo-50' },
  'org.updated':    { icon: Building2,   label: 'updated org',     tone: 'text-slate-700 bg-slate-100' },
  'org.deleted':    { icon: Building2,   label: 'deleted org',     tone: 'text-rose-700 bg-rose-50' },
};

function presentationFor(action) {
  return ACTION_PRESENTATION[action] || {
    icon: Activity,
    label: action,
    tone: 'text-slate-700 bg-slate-100',
  };
}

/**
 * Concise relative-time formatter. Avoids pulling in date-fns
 * just for "5m ago" — this is all we need on this strip.
 */
function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  // Beyond a week the strip stops being "recent" — fall back to a
  // short locale date.
  return new Date(iso).toLocaleDateString();
}

/**
 * Pull a human-readable noun out of an audit row. The audit log
 * stores actor/target metadata in different shapes depending on the
 * action that wrote it — be defensive.
 */
function describe(row) {
  const target = row.target_name || row.target_email || row.target_id || '';
  if (target) return target;
  // Some role-change rows omit a target_name and put the email in metadata.
  return row.metadata?.target_email || row.metadata?.role || '';
}

// How often the strip auto-refreshes when the tab is in the
// foreground. Tuned to feel "live enough" for incident response
// without spamming the audit endpoint — 60s is the sweet spot
// (matches the cadence of polling tiles elsewhere in the app).
const REFRESH_MS = 60_000;
// How long a freshly-arrived entry pulses for before fading back
// to the steady-state row treatment. ~12s gives an admin enough
// time to glance at the strip after switching tabs.
const NEW_ENTRY_PULSE_MS = 12_000;

export default function RecentAdminActivityStrip({ onOpenAuditLog }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // IDs that should pulse on the next render. Reset on a timer so
  // the dot fades to nothing without forcing a re-render storm.
  const [newIds, setNewIds] = useState(() => new Set());
  // Tracked across renders so the refresh callback can diff "rows
  // we knew about" vs "rows we just learned about" without making
  // `rows` itself a dependency (which would re-create the polling
  // timer on every refresh and double-fire requests).
  const seenIdsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      // Fetch each admin-relevant category and merge — the audit
      // endpoint takes one category at a time. Three small queries
      // are cheaper than a no-filter query that pulls back hundreds
      // of irrelevant rows just so we can client-filter to 5.
      const [usersRes, rolesRes, orgsRes] = await Promise.all([
        api.get('/rbac/audit-logs', { params: { category: 'users', limit: 5 } }),
        api.get('/rbac/audit-logs', { params: { category: 'roles', limit: 5 } }),
        api.get('/rbac/audit-logs', { params: { category: 'orgs',  limit: 5 } }),
      ]);
      const merged = [
        ...(usersRes.data?.logs || []),
        ...(rolesRes.data?.logs || []),
        ...(orgsRes.data?.logs  || []),
      ];
      // Newest first, then trim — backend returns each query already
      // sorted, but the merge needs a re-sort to interleave correctly.
      // The audit row's timestamp lives in `created_at`; some legacy
      // callers also expose `timestamp`, so honor either.
      merged.sort((a, b) => {
        const ta = new Date(a.created_at || a.timestamp).getTime();
        const tb = new Date(b.created_at || b.timestamp).getTime();
        return tb - ta;
      });
      const top = merged.slice(0, 5);

      // Diff against what we've already shown so newly-arrived rows
      // pulse and previously-seen rows render as steady-state. The
      // very first load doesn't pulse anything (admin just landed
      // here — every row is "new" but pulsing the entire strip would
      // be visual noise, not signal).
      if (!isFirstLoadRef.current) {
        const arrived = top
          .map((r) => r.id)
          .filter((id) => id && !seenIdsRef.current.has(id));
        if (arrived.length) {
          setNewIds((prev) => {
            const next = new Set(prev);
            arrived.forEach((id) => next.add(id));
            return next;
          });
          // Clear the pulse on each newly-arrived row after the
          // configured window. Captured at schedule time so a
          // subsequent refresh can't double-clear.
          setTimeout(() => {
            setNewIds((prev) => {
              const next = new Set(prev);
              arrived.forEach((id) => next.delete(id));
              return next;
            });
          }, NEW_ENTRY_PULSE_MS);
        }
      }

      seenIdsRef.current = new Set(top.map((r) => r.id).filter(Boolean));
      isFirstLoadRef.current = false;
      setRows(top);
    } catch (err) {
      if (err?.response?.status === 403) {
        setRows([]);
      } else {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + polling loop. The interval is paused while the
  // tab is hidden (`document.hidden`) so a backgrounded admin page
  // doesn't keep poking the audit endpoint, and a refresh fires
  // immediately on visibilitychange so the admin sees up-to-date
  // state the moment they Cmd-Tab back.
  useEffect(() => {
    refresh();
    let timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refresh();
    }, REFRESH_MS);

    const onVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        refresh();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      clearInterval(timer);
      timer = null;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [refresh]);

  if (loading) return null;
  if (error) return null;
  if (!rows.length) return null;

  return (
    <div
      className="mb-6 rounded-lg border border-slate-200 bg-white shadow-sm"
      data-testid="recent-admin-activity-strip"
    >
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60 rounded-t-lg">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
          <Activity size={12} className="text-slate-500" />
          Recent admin activity
          {/* "Live" tag with a steady pulse dot — confirms to admins
              that the strip is auto-refreshing rather than stale. */}
          <span
            className="ml-1 inline-flex items-center gap-1 normal-case tracking-normal text-[10px] font-medium text-emerald-600"
            data-testid="activity-strip-live-indicator"
            title={`Auto-refreshes every ${Math.round(REFRESH_MS / 1000)}s`}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
            </span>
            Live
          </span>
        </div>
        {onOpenAuditLog && (
          <button
            type="button"
            onClick={onOpenAuditLog}
            className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-900"
            data-testid="open-audit-log-link"
          >
            Open Audit Log
            <ArrowRight size={12} />
          </button>
        )}
      </div>

      <ol className="divide-y divide-slate-100">
        {rows.map((row, idx) => {
          const { icon: Icon, label, tone } = presentationFor(row.action);
          const subject = describe(row);
          const isNew = !!row.id && newIds.has(row.id);
          return (
            <li
              key={row.id || `${row.action}-${row.created_at || row.timestamp}-${idx}`}
              className={
                'flex items-center justify-between gap-3 px-4 py-2.5 text-sm transition-colors '
                + (isNew
                  ? 'bg-amber-50/70 hover:bg-amber-50'
                  : 'hover:bg-slate-50/40')
              }
              data-testid={`recent-admin-activity-row-${idx}`}
              data-new-entry={isNew ? 'true' : 'false'}
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tone}`}>
                  <Icon size={14} />
                </span>
                <div className="min-w-0 flex items-center gap-2">
                  <p className="truncate text-slate-800">
                    <span className="font-medium">
                      {row.actor_name || row.actor_email || 'Unknown admin'}
                    </span>
                    <span className="text-slate-500"> · {label}</span>
                    {subject && (
                      <span className="text-slate-700"> · <span className="font-mono text-[12.5px]">{subject}</span></span>
                    )}
                  </p>
                  {isNew && (
                    <span
                      className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
                      data-testid={`new-entry-badge-${idx}`}
                    >
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75"></span>
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-600"></span>
                      </span>
                      New
                    </span>
                  )}
                </div>
              </div>
              <span
                className="shrink-0 text-xs text-slate-400 tabular-nums"
                title={row.created_at || row.timestamp}
              >
                {relativeTime(row.created_at || row.timestamp)}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
